import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import {
  applicationStatusOptions,
  getApplicationStatusLabel,
} from "../../../helpers/jobPortalMeta";
import GeneratedResumeCard from "../../components/GeneratedResumeCard";

const STATUS_STYLES = {
  0: { bg: "#f1f3f5", color: "#6c757d" },
  1: { bg: "#e7f1ff", color: "#0d6efd" },
  2: { bg: "#fff3e0", color: "#fd7e14" },
  3: { bg: "#e6f4ed", color: "#198754" },
  4: { bg: "#e6f4ed", color: "#198754" },
  5: { bg: "#fce8f3", color: "#c2185b" },
  6: { bg: "#f1f3f5", color: "#495057" },
};

const SUMMARY_CARDS = [
  {
    key: "total",
    label: "Total Applicants",
    icon: "bi-people-fill",
    color: "#6f42c1",
    bg: "#f0ebff",
  },
  {
    key: "underReview",
    label: "Under Review",
    icon: "bi-eye-fill",
    color: "#0d6efd",
    bg: "#e7f1ff",
  },
  {
    key: "shortlisted",
    label: "Shortlisted",
    icon: "bi-star-fill",
    color: "#fd7e14",
    bg: "#fff3e0",
  },
  {
    key: "selected",
    label: "Selected",
    icon: "bi-check-circle-fill",
    color: "#198754",
    bg: "#e6f4ed",
  },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "portal", label: "Portal" },
  { value: "direct", label: "Direct" },
  { value: "referral", label: "Referral" },
  { value: "admin", label: "Admin" },
  { value: "imported", label: "Imported" },
];

const HAS_RESUME_OPTIONS = [
  { value: "", label: "All Resumes" },
  { value: "yes", label: "Has Resume" },
  { value: "no", label: "No Resume" },
];

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const RecruiterApplicants = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [resumeFilter, setResumeFilter] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [applications, setApplications] = useState([]);
  const [summary, setSummary] = useState({});
  const [drafts, setDrafts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [resumePreviewApplicant, setResumePreviewApplicant] = useState(null);
  const [canViewApplicantPremiumDetails, setCanViewApplicantPremiumDetails] =
    useState(true);
  const [subscription, setSubscription] = useState(null);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (statusFilter !== "") params.set("status", String(statusFilter));
      if (sourceFilter) params.set("source", sourceFilter);

      const response = await axiosInstance.get(
        `/job-portal/jobs/${id}/applicants?${params.toString()}`,
        authConfig
      );
      const nextApplications = response.data?.data?.applications || [];
      setApplications(nextApplications);
      setSummary(response.data?.data?.summary || {});
      setCanViewApplicantPremiumDetails(
        response.data?.data?.canViewApplicantPremiumDetails !== false
      );
      setSubscription(response.data?.data?.subscription || null);
      setDrafts(
        nextApplications.reduce((acc, item) => {
          acc[item.id] = {
            status: item.status,
            recruiterNotes: item.recruiterNotes || "",
            internalNotes: item.internalNotes || "",
            interviewDate: item.interviewDate
              ? String(item.interviewDate).slice(0, 10)
              : "",
          };
          return acc;
        }, {})
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load applicants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants();
  }, [id, statusFilter, sourceFilter]);

  const stateOptions = useMemo(() => {
    return Array.from(
      new Set(
        applications
          .map((application) => application.applicant?.location?.state)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [applications]);

  const cityOptions = useMemo(() => {
    return Array.from(
      new Set(
        applications
          .filter((application) => {
            if (!stateFilter) return true;
            return (
              String(application.applicant?.location?.state || "").toLowerCase() ===
              stateFilter.toLowerCase()
            );
          })
          .map((application) => application.applicant?.location?.city)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [applications, stateFilter]);

  const visibleApplications = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

      return applications.filter((application) => {
      const applicant = application.applicant || {};
      const location = applicant.location || {};
      const haystack = [
        applicant.name,
        applicant.email,
        applicant.phone,
        applicant.specialization,
        location.city,
        location.state,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (normalizedSearch && !haystack.includes(normalizedSearch)) return false;
      if (
        stateFilter &&
        String(location.state || "").toLowerCase() !== stateFilter.toLowerCase()
      ) {
        return false;
      }
      if (
        cityFilter &&
        String(location.city || "").toLowerCase() !== cityFilter.toLowerCase()
      ) {
        return false;
      }
      if (resumeFilter === "yes" && !applicant.resumeUrl) return false;
      if (resumeFilter === "no" && applicant.resumeUrl) return false;

      const appliedDate = application.appliedAt
        ? new Date(application.appliedAt)
        : null;
      if (appliedFrom && appliedDate && appliedDate < new Date(appliedFrom)) {
        return false;
      }
      if (appliedTo && appliedDate) {
        const endDate = new Date(appliedTo);
        endDate.setHours(23, 59, 59, 999);
        if (appliedDate > endDate) return false;
      }

      return true;
    });
  }, [
    applications,
    searchTerm,
    stateFilter,
    cityFilter,
    resumeFilter,
    appliedFrom,
    appliedTo,
  ]);

  const updateDraftField = (appId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [appId]: { ...prev[appId], [field]: value },
    }));
  };

  const clearLocalFilters = () => {
    setSearchTerm("");
    setStateFilter("");
    setCityFilter("");
    setResumeFilter("");
    setAppliedFrom("");
    setAppliedTo("");
  };

  const saveApplication = async (appId) => {
    if (!canViewApplicantPremiumDetails) {
      toast.error(
        "An active recruiter plan is required to update applicant details"
      );
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      const draft = drafts[appId];
      await axiosInstance.put(
        `/job-portal/applications/${appId}/status`,
        {
          status: Number(draft.status),
          recruiterNotes: draft.recruiterNotes,
          internalNotes: draft.internalNotes,
          interviewDate: draft.interviewDate || null,
          note: "Updated from recruiter dashboard",
        },
        authConfig
      );
      toast.success("Application updated");
      fetchApplicants();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update applicant");
    }
  };

  return (
    <>
      <style>{`
        .applicant-card {
          border: 1.5px solid #e9ecef;
          border-radius: 14px;
          margin-bottom: 12px;
          transition: box-shadow 0.18s;
          overflow: hidden;
          background: #fff;
        }
        .applicant-card:hover { box-shadow: 0 4px 18px rgba(0,0,0,0.08); }
        .applicant-card-header {
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          cursor: pointer;
          background: #fff;
        }
        .applicant-card-header:hover { background: #fafbfc; }
        .applicant-card-body {
          padding: 0 20px 20px;
          border-top: 1px solid #f1f3f5;
          background: #fafbfc;
        }
        .candidate-avatar {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border-radius: 20px;
          font-size: 11.5px;
          font-weight: 600;
          padding: 4px 12px;
          text-transform: capitalize;
        }
        .sum-card {
          border-radius: 14px;
          padding: 18px;
          transition: transform 0.18s, box-shadow 0.18s;
        }
        .sum-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08) !important; }
        .sum-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          margin-bottom: 10px;
        }
        .sum-val { font-size: 24px; font-weight: 700; line-height: 1; margin-bottom: 3px; }
        .sum-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #868e96;
        }
        .notes-textarea,
        .status-select,
        .date-input,
        .filter-input,
        .filter-select {
          border: 1.5px solid #e9ecef;
          border-radius: 10px;
          padding: 9px 14px;
          font-size: 13.5px;
          width: 100%;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
        }
        .notes-textarea {
          min-height: 110px;
          resize: vertical;
        }
        .notes-textarea:focus,
        .status-select:focus,
        .date-input:focus,
        .filter-input:focus,
        .filter-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.10);
        }
        .filter-card {
          border: 1px solid #e9ecef;
          border-radius: 16px;
          background: #fff;
          padding: 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
        }
        .filter-label {
          display: block;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #6c757d;
          margin-bottom: 8px;
        }
        .action-btn { border-radius: 8px; font-size: 13px; font-weight: 500; }
        .expanded-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #868e96;
          margin-bottom: 6px;
        }
        .resume-preview-modal {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          z-index: 1080;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .resume-preview-dialog {
          background: #fff;
          border-radius: 18px;
          width: min(1100px, 100%);
          max-height: calc(100vh - 40px);
          overflow: auto;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
        }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ letterSpacing: "-0.3px" }}>
                <i className="bi bi-people-fill text-primary me-2" />
                Applicants
              </h4>
              <p className="text-muted mb-0 small">
                Review candidates, filter the pipeline, and update statuses.
              </p>
            </div>
            <Link to="/recruiter/jobs" className="btn btn-outline-primary action-btn">
              <i className="bi bi-arrow-left me-2" />
              Back to Jobs
            </Link>
          </div>
        </div>

        {!loading && !canViewApplicantPremiumDetails && (
          <div className="alert alert-warning border-0 shadow-sm mb-4">
            Active recruiter plan required to unlock contact details and applicant actions.
            <Link to="/recruiter/plans" className="ms-2 alert-link">
              Buy Plan
            </Link>
            {subscription?.statusLabel ? (
              <span className="ms-2 small text-muted">
                Current status: {subscription.statusLabel}
              </span>
            ) : null}
          </div>
        )}

        <div className="row g-3 mb-4">
          {SUMMARY_CARDS.map((card) => (
            <div className="col-md-3" key={card.key}>
              <div className="sum-card shadow-sm" style={{ background: card.bg }}>
                {loading ? (
                  <div className="placeholder-glow">
                    <span
                      className="placeholder rounded d-block mb-2"
                      style={{ width: 40, height: 40 }}
                    />
                    <span
                      className="placeholder col-4 rounded d-block mb-1"
                      style={{ height: 24 }}
                    />
                    <span
                      className="placeholder col-7 rounded d-block"
                      style={{ height: 12 }}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      className="sum-icon"
                      style={{ background: `${card.color}1a`, color: card.color }}
                    >
                      <i className={`bi ${card.icon}`} />
                    </div>
                    <div className="sum-val" style={{ color: card.color }}>
                      {summary[card.key] || 0}
                    </div>
                    <div className="sum-label">{card.label}</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="filter-card mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h6 className="mb-1">Applicant Filters</h6>
              <p className="text-muted mb-0 small">
                Server filters: status and source. Local filters: search, location,
                resume, and applied date.
              </p>
            </div>
            <button type="button" className="btn btn-light action-btn" onClick={clearLocalFilters}>
              Clear Local Filters
            </button>
          </div>

          <div className="row g-3">
            <div className="col-lg-4">
              <label className="filter-label">Search</label>
              <input
                className="filter-input"
                placeholder="Name, email, phone, specialization"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="filter-label">Status</label>
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {applicationStatusOptions.map((option) => (
                  <option
                    key={option.value === "" ? "all-status" : option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="filter-label">Source</label>
              <select
                className="filter-select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option
                    key={option.value === "" ? "all-source" : option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="filter-label">State</label>
              <select
                className="filter-select"
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setCityFilter("");
                }}
              >
                <option value="">All States</option>
                {stateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="filter-label">City</label>
              <select
                className="filter-select"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <option value="">All Cities</option>
                {cityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-4">
              <label className="filter-label">Resume</label>
              <select
                className="filter-select"
                value={resumeFilter}
                onChange={(e) => setResumeFilter(e.target.value)}
              >
                {HAS_RESUME_OPTIONS.map((option) => (
                  <option
                    key={option.value === "" ? "all-resume" : option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="filter-label">Applied From</label>
              <input
                type="date"
                className="date-input"
                value={appliedFrom}
                onChange={(e) => setAppliedFrom(e.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <label className="filter-label">Applied To</label>
              <input
                type="date"
                className="date-input"
                value={appliedTo}
                onChange={(e) => setAppliedTo(e.target.value)}
              />
            </div>
            <div className="col-lg-8 d-flex align-items-end">
              <div className="text-muted small">
                Showing <strong>{visibleApplications.length}</strong> of{" "}
                <strong>{applications.length}</strong> applicants for this job.
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex flex-column gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="applicant-card placeholder-glow p-4">
                <div className="d-flex gap-3 align-items-center">
                  <span
                    className="placeholder rounded-3"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="flex-grow-1">
                    <span className="placeholder col-4 rounded d-block mb-2" />
                    <span className="placeholder col-6 rounded d-block" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : visibleApplications.length === 0 ? (
          <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
            <div className="card-body text-center py-5">
              <i
                className="bi bi-person-x text-muted"
                style={{ fontSize: 48, opacity: 0.25 }}
              />
              <p className="text-muted mt-3 mb-0 small">
                No applicants found for the current filters.
              </p>
            </div>
          </div>
        ) : (
          visibleApplications.map((application) => {
            const applicant = application.applicant || {};
            const location = applicant.location || {};
            const draft = drafts[application.id] || {};
            const isExpanded = expandedId === application.id;
            const resumeLink = applicant.resumeUrl?.startsWith("public")
              ? `${weburl}${applicant.resumeUrl.replace("public", "")}`
              : applicant.resumeUrl || "";
            const usesAutoResume = applicant.resumeMode === "auto";
            const statusStyle = STATUS_STYLES[application.status] || STATUS_STYLES[0];
            const initials = (applicant.name || "?")
              .split(" ")
              .map((word) => word[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();
            const avatarColors = [
              ["#e7f1ff", "#0d6efd"],
              ["#f0ebff", "#6f42c1"],
              ["#e6f4ed", "#198754"],
              ["#fff3e0", "#fd7e14"],
              ["#fce8f3", "#c2185b"],
            ];
            const [avatarBg, avatarFg] =
              avatarColors[(applicant.name?.charCodeAt(0) || 0) % avatarColors.length];

            return (
              <div className="applicant-card" key={application.id}>
                <div
                  className="applicant-card-header"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : application.id)
                  }
                >
                  <div className="d-flex gap-3 align-items-center">
                    <div
                      className="candidate-avatar"
                      style={{ background: avatarBg, color: avatarFg }}
                    >
                      {initials}
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        {applicant.name || "Candidate"}
                      </div>
                      {canViewApplicantPremiumDetails ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          <i className="bi bi-envelope me-1" />
                          {applicant.email || "No email"}
                          {applicant.phone ? (
                            <>
                              <span className="mx-2">|</span>
                              <i className="bi bi-telephone me-1" />
                              {applicant.phone}
                            </>
                          ) : null}
                        </div>
                      ) : (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          <i className="bi bi-lock me-1" />
                          Contact details locked
                        </div>
                      )}
                      <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                        <span
                          className="status-pill"
                          style={{
                            background: statusStyle.bg,
                            color: statusStyle.color,
                          }}
                        >
                          <span
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "currentColor",
                              display: "inline-block",
                            }}
                          />
                          {getApplicationStatusLabel(application.status)}
                        </span>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                          Applied {formatDate(application.appliedAt) || "recently"}
                        </span>
                        {(location.city || location.state) && (
                          <span className="text-muted" style={{ fontSize: 11 }}>
                            <i className="bi bi-geo-alt me-1" />
                            {[location.city, location.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                        {application.source && (
                          <span className="text-muted" style={{ fontSize: 11 }}>
                            <i className="bi bi-diagram-3 me-1" />
                            {application.source}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="d-flex gap-2 align-items-center flex-wrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canViewApplicantPremiumDetails ? (
                      <Link
                        to={`/user-job-seeker/${application.applicantId}`}
                        className="btn btn-sm btn-outline-primary action-btn"
                      >
                        <i className="bi bi-person me-1" />
                        Profile
                      </Link>
                    ) : null}
                    {canViewApplicantPremiumDetails && usesAutoResume ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setResumePreviewApplicant(applicant);
                        }}
                      >
                        <i className="bi bi-file-earmark-text me-1" />
                        Resume
                      </button>
                    ) : canViewApplicantPremiumDetails && resumeLink ? (
                      <a
                        href={resumeLink}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary action-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="bi bi-file-earmark-text me-1" />
                        Resume
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        isExpanded ? "btn-primary" : "btn-outline-dark"
                      } action-btn`}
                    >
                      <i
                        className={`bi ${
                          isExpanded ? "bi-chevron-up" : "bi-chevron-down"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="applicant-card-body pt-4">
                    {canViewApplicantPremiumDetails ? (
                      <div className="row g-3">
                        <div className="col-md-3">
                          <div className="expanded-label">Update Status</div>
                          <select
                            className="status-select"
                            value={draft.status ?? application.status}
                            onChange={(e) =>
                              updateDraftField(application.id, "status", e.target.value)
                            }
                          >
                            {applicationStatusOptions
                              .filter((option) => option.value !== "")
                              .map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="col-md-3">
                          <div className="expanded-label">Interview Date</div>
                          <input
                            type="date"
                            className="date-input"
                            value={draft.interviewDate || ""}
                            onChange={(e) =>
                              updateDraftField(
                                application.id,
                                "interviewDate",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="col-md-3">
                          <div className="expanded-label">Current Status</div>
                          <div className="pt-2 fw-semibold">
                            {getApplicationStatusLabel(application.status)}
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="expanded-label">Source</div>
                          <div className="pt-2 text-capitalize">
                            {application.source || "portal"}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="expanded-label">Recruiter Notes</div>
                          <textarea
                            className="notes-textarea"
                            value={draft.recruiterNotes || ""}
                            onChange={(e) =>
                              updateDraftField(
                                application.id,
                                "recruiterNotes",
                                e.target.value
                              )
                            }
                            placeholder="Visible notes for the hiring team"
                          />
                        </div>

                        <div className="col-md-6">
                          <div className="expanded-label">Internal Notes</div>
                          <textarea
                            className="notes-textarea"
                            value={draft.internalNotes || ""}
                            onChange={(e) =>
                              updateDraftField(
                                application.id,
                                "internalNotes",
                                e.target.value
                              )
                            }
                            placeholder="Private decision notes"
                          />
                        </div>

                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="small text-muted">
                            {applicant.specialization ? (
                              <>
                                <strong>Specialization:</strong> {applicant.specialization}
                              </>
                            ) : (
                              "Candidate profile details can be reviewed from the profile link."
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary action-btn"
                            onClick={() => saveApplication(application.id)}
                          >
                            <i className="bi bi-save me-2" />
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="small text-muted">
                        Active recruiter plan required to unlock contact details and applicant actions.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {resumePreviewApplicant && (
          <div
            className="resume-preview-modal"
            onClick={() => setResumePreviewApplicant(null)}
          >
            <div
              className="resume-preview-dialog"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                <div>
                  <h5 className="mb-1">Generated Resume</h5>
                  <div className="small text-muted">
                    {resumePreviewApplicant.name || "Candidate"}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setResumePreviewApplicant(null)}
                >
                  Close
                </button>
              </div>
              <div className="p-3">
                <GeneratedResumeCard
                  profile={{
                    username: resumePreviewApplicant.name || "",
                    email: resumePreviewApplicant.email || "",
                    phone: resumePreviewApplicant.phone || "",
                    address: resumePreviewApplicant.address || "",
                    city: resumePreviewApplicant.location?.city || "",
                    statename: resumePreviewApplicant.location?.state || "",
                    experience: resumePreviewApplicant.experience ?? "",
                    linkedInProfile: resumePreviewApplicant.linkedInProfile || "",
                    highestMedicalDegree:
                      resumePreviewApplicant.highestMedicalDegree || "",
                    institutionUniversity:
                      resumePreviewApplicant.institutionUniversity || "",
                    yearOfGraduation:
                      resumePreviewApplicant.yearOfGraduation || "",
                    jobLocation:
                      resumePreviewApplicant.jobPreferences?.preferredLocation || "",
                    employmentType:
                      resumePreviewApplicant.jobPreferences?.employmentType || "",
                    salaryExpectation:
                      resumePreviewApplicant.jobPreferences?.salaryExpectation || "",
                    ProfessionalAchievements:
                      resumePreviewApplicant.professionalSummary || "",
                    WorkExperience:
                      resumePreviewApplicant.workExperience || [],
                    KeyAchievements:
                      resumePreviewApplicant.keyAchievements || [],
                    specialization:
                      resumePreviewApplicant.jobPreferences?.functionalAreas || [],
                    profile_url: resumePreviewApplicant.profileUrl || "",
                  }}
                  specializationOptions={[]}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default RecruiterApplicants;
